import {
  Grid,
  Stack,
  Typography,
  Card,
  CardContent,
  Button,
} from "@mui/material";
import { Edit, Save } from "@mui/icons-material";
import React, { useEffect, useState } from "react";
import { useApiClient } from "../../../api";
import { useAuthenticationStore } from "../../../stores/auth";
import GeneralLoading from "../../../shared-components/Loading";
import SchoolSearchAutocomplete from "../../school/components/SchoolSearchAutocomplete";
import { StripeSubscriptionManagement } from "./StripeSubscriptionManagement";

const isIndependentSingleSubjectUser = ({ user }) => {
  // TODO: future - this might not be the cleanest way of checking
  const info = user.info;
  if (!info) return false;

  const isIndependent =
    info.subscribed && info.subscriptionStatus === "independent";
  const teacher = info.teacher;

  const hasOneOrNoSubject = !teacher?.subjects || teacher.subjects.length < 2;

  return isIndependent && hasOneOrNoSubject;
};

export const TeacherSettings = ({ user, editable = false }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [existingData, setExistingData] = useState(null);
  const [upgrading, setUpgrading] = useState(false);
  const [school, setSchool] = useState(null);
  const apiClient = useApiClient();
  const { updateUserInfo } = useAuthenticationStore();

  useEffect(() => {
    const fetchExistingData = async () => {
      try {
        const data = await apiClient.users.requestExistingRegistrationData();
        setExistingData(data || null);
      } catch (error) {
        console.error("Error fetching data:", error);
        setExistingData(null);
      }
    };
    fetchExistingData();
  }, []);

  useEffect(() => {
    if (user) {
      setSchool(user.info.activeContext?.roleAssignment?.school || null);
    }
  }, [user]);

  const handleClick = async () => {
    if (isEditing) {
      await onSave();
      setIsEditing(false);
    } else {
      setIsEditing(true);
    }
  };

  const onSave = async () => {
    try {
      setSubmitting(true);
      const result = await apiClient.users.updateTeacherInfo({
        school: school.id,
      });
      setSubmitting(false);
      if (result.success) {
        const userData = await apiClient.users.me();
        updateUserInfo(userData);
      }
      setIsEditing(false);
    } catch (error) {
      console.error("Error saving data:", error);
    } finally {
      setSubmitting(false);
    }
  };

  if (!existingData) {
    return <GeneralLoading />;
  }

  if (submitting) {
    return <GeneralLoading message={"Saving your changes..."} />;
  }

  return (
    <Card variant={"shadow"}>
      <CardContent>
        <Stack
          direction={"row"}
          spacing={2}
          alignItems={"center"}
          sx={{ mb: 2 }}
        >
          <Typography variant="h3" mb={2}>
            Teacher settings
          </Typography>

          {editable && (
            <Button
              variant={"card-action"}
              onClick={handleClick}
              startIcon={isEditing ? <Save /> : <Edit />}
            >
              {isEditing ? "Save" : "Edit"}
            </Button>
          )}
        </Stack>
        <Grid container direction={"column"} spacing={2}>
          <Grid size={12}>
            {isEditing ? (
              <SchoolSearchAutocomplete
                school={school}
                onChange={(_, value) => setSchool(value)}
              />
            ) : (
              <>
                <Typography fontWeight="bold">School</Typography>
                <Typography>
                  {user.info.activeContext?.roleAssignment?.school.name}
                </Typography>
              </>
            )}
          </Grid>
          <Grid size={12}>
            <Stack
              direction={"row"}
              spacing={2}
              alignItems={"center"}
              justifyContent={"space-between"}
            >
              {isIndependentSingleSubjectUser({ user }) && (
                <StripeSubscriptionManagement
                  redirecting={upgrading}
                  setRedirecting={setUpgrading}
                  label={"Upgrade"}
                />
              )}
            </Stack>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};
